import React from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Alert } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import moment from "moment";

const getStatusColor = (variant) => {
  const colors = {
    primary: "#0d6efd",
    success: "#198754",
    warning: "#ffc107",
    danger: "#ff5454",
    info: "#0dcaf0",
    secondary: "#6c757d",
  };
  return colors[variant] || colors.primary;
};

const StatCard = ({ title, value, icon, variant = "primary", link }) => (
  <Card className="mb-3 h-100">
    <Card.Body className="p-3 text-center">
      <div className="mb-2">
        <i
          className={`fa ${icon} fa-2x`}
          style={{ color: getStatusColor(variant), opacity: 0.8 }}
        ></i>
      </div>
      <h3 className="mb-1" style={{ color: getStatusColor(variant) }}>
        {value}
      </h3>
      <small className="text-muted">{title}</small>
      {link && <Link to={link} className="stretched-link" />}
    </Card.Body>
  </Card>
);

const MaintenanceDashboard = ({
  stats,
  upcoming,
  overdue,
  loading,
  error,
}) => {
  if (loading && !stats) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Alert variant="danger">
        <i className="fa fa-exclamation-circle me-2"></i>
        Error loading dashboard: {error.message || "Unknown error"}
      </Alert>
    );
  }

  return (
    <div className="maintenance-dashboard">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fa fa-wrench me-2"></i>
          Maintenance Dashboard
        </h4>
        <div>
          <Link to="/maintenance/machines/new" className="btn btn-primary btn-sm me-2">
            <i className="fa fa-plus me-1"></i> Add Machine
          </Link>
          <Link to="/maintenance/records/new" className="btn btn-success btn-sm">
            <i className="fa fa-plus me-1"></i> Schedule Maintenance
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="mb-3">
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="Total Machines"
            value={stats?.total_machines || 0}
            icon="fa-cogs"
            variant="primary"
            link="/maintenance/machines"
          />
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="Active Machines"
            value={stats?.active_machines || 0}
            icon="fa-check-circle"
            variant="success"
          />
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="Under Maintenance"
            value={stats?.machines_under_maintenance || 0}
            icon="fa-wrench"
            variant="warning"
          />
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <StatCard
            title="Overdue"
            value={stats?.overdue_maintenance_count || 0}
            icon="fa-exclamation-triangle"
            variant="danger"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4} sm={6} className="mb-2">
          <StatCard
            title="Due This Month"
            value={stats?.maintenance_due_count || 0}
            icon="fa-calendar"
            variant="info"
          />
        </Col>
        <Col md={4} sm={6} className="mb-2">
          <StatCard
            title="Completed This Month"
            value={stats?.completed_this_month || 0}
            icon="fa-check"
            variant="success"
          />
        </Col>
        <Col md={4} sm={6} className="mb-2">
          <StatCard
            title="Cost This Month"
            value={`$${parseFloat(stats?.total_cost_this_month || 0).toLocaleString()}`}
            icon="fa-dollar"
            variant="secondary"
          />
        </Col>
      </Row>

      <Row>
        {/* Overdue Maintenance */}
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header className="reports-filter d-flex justify-content-between align-items-center">
              <span>
                <i className="fa fa-exclamation-triangle me-2" style={{ color: "#ff5454" }}></i>
                Overdue Maintenance
              </span>
              {overdue.length > 0 && (
                <span className="badge badge-pill" style={{ backgroundColor: "#ff5454" }}>
                  {overdue.length}
                </span>
              )}
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: "280px", overflowY: "auto" }}>
              {overdue.length === 0 ? (
                <div className="shipment-list--empty text-center py-4">
                  <i className="fa fa-check-circle fa-2x mb-2" style={{ color: "#198754" }}></i>
                  <p className="mb-0 text-muted">No overdue maintenance</p>
                </div>
              ) : (
                <table className="table table-striped table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th>Due Date</th>
                      <th>Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdue.map((item) => (
                      <tr key={item.machine_id}>
                        <td>
                          <Link to={`/maintenance/machines/${item.machine_id}`}>
                            {item.machine_name}
                          </Link>
                          <br />
                          <small className="text-muted">{item.location}</small>
                        </td>
                        <td>{moment(item.next_maintenance_date).format("MMM D, YYYY")}</td>
                        <td>
                          <span className="badge badge-pill" style={{ backgroundColor: "#ff5454" }}>
                            {Math.abs(item.days_until_due)} days
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Upcoming Maintenance */}
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header className="reports-filter d-flex justify-content-between align-items-center">
              <span>
                <i className="fa fa-calendar me-2" style={{ color: "#ffc107" }}></i>
                Upcoming Maintenance (30 Days)
              </span>
              {upcoming.length > 0 && (
                <span className="badge badge-pill" style={{ backgroundColor: "#6c757d" }}>
                  {upcoming.length}
                </span>
              )}
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: "280px", overflowY: "auto" }}>
              {upcoming.length === 0 ? (
                <div className="shipment-list--empty text-center py-4">
                  <i className="fa fa-calendar-o fa-2x mb-2 text-muted"></i>
                  <p className="mb-0 text-muted">No upcoming maintenance scheduled</p>
                </div>
              ) : (
                <table className="table table-striped table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th>Due Date</th>
                      <th>Days Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming.map((item) => (
                      <tr key={item.machine_id}>
                        <td>
                          <Link to={`/maintenance/machines/${item.machine_id}`}>
                            {item.machine_name}
                          </Link>
                          <br />
                          <small className="text-muted">{item.location}</small>
                        </td>
                        <td>{moment(item.next_maintenance_date).format("MMM D, YYYY")}</td>
                        <td>
                          <span
                            className="badge badge-pill"
                            style={{
                              backgroundColor: item.days_until_due <= 7 ? "#ffc107" : "#0dcaf0",
                              color: item.days_until_due <= 7 ? "#000" : "#fff",
                            }}
                          >
                            {item.days_until_due} days
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Quick Links */}
      <Card>
        <Card.Header className="reports-filter">
          <i className="fa fa-link me-2"></i>
          Quick Links
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="mb-2">
            <Col md={3} sm={6} className="mb-2">
              <Link to="/maintenance/tasks" className="btn btn-outline-warning btn-sm w-100">
                <i className="fa fa-calendar-check-o me-1"></i> Today's Tasks
              </Link>
            </Col>
            <Col md={3} sm={6} className="mb-2">
              <Link to="/maintenance/machines" className="btn btn-outline-primary btn-sm w-100">
                <i className="fa fa-cogs me-1"></i> Machines
              </Link>
            </Col>
            <Col md={3} sm={6} className="mb-2">
              <Link to="/maintenance/facilities" className="btn btn-outline-info btn-sm w-100">
                <i className="fa fa-building me-1"></i> Facilities
              </Link>
            </Col>
            <Col md={3} sm={6} className="mb-2">
              <Link to="/maintenance/records" className="btn btn-outline-success btn-sm w-100">
                <i className="fa fa-list me-1"></i> Records
              </Link>
            </Col>
          </Row>
          <Row>
            <Col md={3} sm={6} className="mb-2 mb-md-0">
              <Link to="/maintenance/task-templates" className="btn btn-outline-secondary btn-sm w-100">
                <i className="fa fa-clipboard me-1"></i> Task Templates
              </Link>
            </Col>
            <Col md={3} sm={6} className="mb-2 mb-md-0">
              <Link to="/maintenance/task-assignments" className="btn btn-outline-secondary btn-sm w-100">
                <i className="fa fa-link me-1"></i> Assignments
              </Link>
            </Col>
            <Col md={3} sm={6} className="mb-2 mb-md-0">
              <Link to="/maintenance/records/service-visit" className="btn btn-outline-secondary btn-sm w-100">
                <i className="fa fa-truck me-1"></i> Service Visit
              </Link>
            </Col>
            <Col md={3} sm={6}>
              <Link to="/maintenance/settings" className="btn btn-outline-secondary btn-sm w-100">
                <i className="fa fa-cog me-1"></i> Settings
              </Link>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default MaintenanceDashboard;
